import {debounceTime, filter, map, merge, Observable, of} from 'rxjs';
import {useRef} from 'react';

import {
  ANIMATION_DURATION,
  useSyncConversationObservable,
} from '@stripe-internal/conversations-ui';
import type {Conversation} from '@stripe-internal/conversations-client';

import {SupportConversationsClient} from '../lib/SupportConversationsClient';

function buildBusyObservable(conversation: Conversation): Observable<boolean> {
  const {client} = conversation;

  if (client instanceof SupportConversationsClient) {
    const obs$ = client.busyObservable();

    const busy$ = obs$.pipe(filter((busy) => busy));
    const idle$ = obs$.pipe(
      filter((busy) => !busy),
      // delay idle events to give time for effect to render/animate
      debounceTime(ANIMATION_DURATION + 100),
    );

    // Also re-check isBusy() when the context changes, so that if the client
    // was busy before transitioning (e.g. assistant -> chat), we re-evaluate
    // and correctly report idle once the transition completes.
    const context$ = conversation.contextObservable();

    return merge(busy$, idle$, context$).pipe(
      // always use current source of truth
      map(() => client.isBusy()),
    );
  }

  return of(false);
}

export function useConversationClientPending(conversation: Conversation) {
  // using a ref, so the observable we use is consistent across renders.
  // this allows us to start with the real current value, but defer idle/settled ("not busy")
  // signals. otherwise, if a re-render happened it could briefly show a settled state before
  // going right back to a busy state if a re-render happened in between 2 back-to-back requests
  const busyObservable = useRef<Observable<boolean> | null>(null);

  return useSyncConversationObservable({
    observable: () => {
      busyObservable.current ??= buildBusyObservable(conversation);
      return busyObservable.current;
    },
    get: () => {
      const {client} = conversation;
      if (client instanceof SupportConversationsClient) {
        return client.isBusy();
      }
      return false;
    },
  });
}
